import { CardBrand, OperationType, Transaction, TransactionStatus } from '../../../domain/transaction';
import {
  addDays,
  eachDayOfInterval,
  endOfDay,
  format,
  isWithinInterval,
  startOfDay,
  startOfMonth,
  startOfWeek,
  startOfYear,
} from 'date-fns';
import {
  COLORS,
  DAYS,
  dayTranslateMap,
  groupTransactionBarModel,
  MONTHS,
  monthTranslateMap,
  TransactionData,
} from '../model/TransactionBarCharProps';
import { TimeFrameChart, TransactionTypeChart } from '../enum/Dashboard.enum';
import { paymentMethodToString } from '../../../domain/payment-method';
import {
  capitalizeEachWord,
  translateCardBrand,
  translateOperationType,
  translateTransactionStatus,
} from '../../../domain/TranslateEnumTypes';
import { Formatter } from '../../../utils/Formatter';

export interface groupTransactionModel {
  key: TransactionTypeChart
  groupMap: Map<string, number>
  labelMap: Map<string, string>
  totalTransactions: number
}

export const chartUtilService = {
  loadBarChartData(transaction: Transaction, model: groupTransactionBarModel) {
    if (transaction?.date_time) {
      const formattedDate = Formatter.toDate(transaction.date_time)
      const transactionDate: Date = formattedDate.date

      const  isWithinRange = model.dateRange && isWithinInterval(transactionDate, model.dateRange)
      let key = null

      switch (model.key) {
        case TimeFrameChart.DAY:
          const [hours] = formattedDate.timeString.split(':').map(Number);
          key = isWithinRange ? hours : key
          break
        case TimeFrameChart.WEEK:
          key = isWithinRange ? translateDay(transactionDate.getDay()) : key
          break
        case TimeFrameChart.MONTH:
          // "MM-dd" con cero a la izquierda para que los días ordenen ascendente (01..31).
          key = isWithinRange ? format(transactionDate, 'MM-dd') : key
          break
        case TimeFrameChart.YEAR:
          key = isWithinRange ? translateMonth(transactionDate.getMonth()) : key
          break
      }

      if (key !== null) {
        if (transaction.status === TransactionStatus.approved )
          model.groupMap.set(key, ((model.groupMap.get(key) || 0) + transaction.gross_amount))
      }
    }
  },

  loadDonutChartData(transaction: Transaction, model: groupTransactionModel) {
    let key: string
    let translateTransactionEnum: ((val: any) => string) | null = null
    let isValidToChart: boolean = false

    switch (model.key) {
      case TransactionTypeChart.PAYMENT_METHOD:
        key = transaction.payment_method_type
        translateTransactionEnum = paymentMethodToString
        isValidToChart = transaction.status === TransactionStatus.approved &&
          transaction.operation_type === OperationType.payment
        break;
      case TransactionTypeChart.STATUS:
        key = transaction.status
        translateTransactionEnum = translateTransactionStatus
        isValidToChart = transaction.operation_type === OperationType.payment
        break;
      case TransactionTypeChart.OPERATION_TYPE:
        key = transaction.operation_type
        translateTransactionEnum = translateOperationType
        isValidToChart = transaction.status === TransactionStatus.approved
        break;
      case TransactionTypeChart.CARD_BRAND:
        key = transaction.card_brand
        translateTransactionEnum = translateCardBrand
        isValidToChart = transaction.status === TransactionStatus.approved &&
          transaction.operation_type === OperationType.payment
        break;
      default:
        key = transaction.payment_method_type
        break;
    }

    if (isValidToChart) {
      model.totalTransactions += 1

      if (model.groupMap.has(key)) {
        model.groupMap.set(key, (model.groupMap.get(key) || 0) + 1)
      } else {
        model.groupMap.set(key, 1)
        model.labelMap.set(key,
          translateTransactionEnum ? translateTransactionEnum(key) : key)
      }
    }
  },

  getDonutChartData(modelGroup: groupTransactionModel[]) {
    const map = new Map<TransactionTypeChart, {}>()

    modelGroup.forEach(model => {
      const groupedData = model.key === TransactionTypeChart.CARD_BRAND ?
        groupCardBrands(model.groupMap) : model.groupMap

      const pieChartData = Array.from(groupedData.entries())
        .map(([key, count], index) => {
          const text = capitalizeEachWord(model.labelMap.get(key) || key)
          return {
            id: text,
            label: text,
            value: ((count / model.totalTransactions) * 100).toFixed(0),
            color: COLORS[index]
          }
        });

      map.set(model.key, pieChartData)
    })

    return map;
  },

  getConfigByTimeFrame(timeFrame: TimeFrameChart): groupTransactionBarModel {
    const today = new Date()
    const config: groupTransactionBarModel = {
      key: timeFrame,
      groupMap: new Map<string | number, number>(),
      dateRange: null
    }

    switch (timeFrame) {
      case TimeFrameChart.DAY:
        config.dateRange = {start: startOfDay(today), end: endOfDay(today)}
        break
      case TimeFrameChart.WEEK:
        config.groupMap = new Map(DAYS.map((day: string) => [day, 0]))
        config.dateRange = {start: startOfWeek(today, {weekStartsOn: 1}), end: endOfDay(today)}
        break
      case TimeFrameChart.MONTH: {
        // Mes en curso: del día 1 a las 00:00 (hora local) hasta hoy. Al quedar dentro del
        // mismo mes calendario nunca supera el límite de 31 días del BFF.
        const monthStart = startOfMonth(today)
        const monthEnd = endOfDay(today)
        config.dateRange = {start: monthStart, end: monthEnd}
        // Se prellenan los días transcurridos del mes (igual que WEEK con los 7 días y YEAR
        // con los 12 meses) para que se muestren aunque no tengan transacciones.
        config.groupMap = new Map(eachDayOfInterval({start: monthStart, end: monthEnd})
          .map((day) => [format(day, 'MM-dd'), 0]))
        break
      }
      case TimeFrameChart.YEAR:
        config.groupMap = new Map(MONTHS.map((month: string) => [month, 0]))
        config.dateRange = {start: startOfYear(today), end: endOfDay(today)}
        break
    }

    return config
  },
  /**
   * Rangos a pedirle al BFF para traer los datos de un `TimeFrameChart`. Para DAY/WEEK/MONTH
   * es un solo rango (el mismo `dateRange` de `getConfigByTimeFrame`, así que el filtro por
   * intervalo de `loadBarChartData` siempre queda consistente con lo que realmente se pidió).
   * YEAR es un caso especial: el BFF rechaza rangos de más de 31 días, así que no se puede
   * pedir el año completo de una sola vez — se arma en trozos de 20 días desde el 1 de enero
   * hasta hoy. OJO: NO se usan meses calendario (1 de marzo - 31 de marzo son 31 días exactos
   * y el BFF los rechaza igual), por eso son trozos fijos de 20 días en vez de "un mes".
   */
  getFetchRangesByTimeFrame(timeFrame: TimeFrameChart): { start: Date, end: Date }[] {
    if (timeFrame === TimeFrameChart.YEAR) {
      const today = new Date()
      const ranges: { start: Date, end: Date }[] = []
      let chunkStart = startOfYear(today)

      while (chunkStart <= today) {
        const chunkEnd = new Date(Math.min(addDays(chunkStart, 19).getTime(), today.getTime()))
        ranges.push({start: startOfDay(chunkStart), end: endOfDay(chunkEnd)})
        chunkStart = addDays(chunkEnd, 1)
      }

      return ranges
    }

    const { dateRange } = chartUtilService.getConfigByTimeFrame(timeFrame)
    return dateRange ? [dateRange] : []
  },
  getBarCharMap(dataForGroup: groupTransactionBarModel[]) {
    const collator = new Intl.Collator(undefined, {numeric: true})
    const barChartMap = new Map<TimeFrameChart, TransactionData[]>()

    dataForGroup.forEach(item => {
      const barCharData: TransactionData[] = convertToBarChartData(item.key, item.groupMap, collator)
      barChartMap.set(item.key, barCharData)
    })

    return barChartMap
  }
}

const groupCardBrands = (map: any): Map<string, number> => {
  const sortBrand = [CardBrand.amex, CardBrand.mastercard, CardBrand.visa, CardBrand.wuzi, CardBrand.otras]
  const groupedMap = new Map<string, number>(sortBrand.map(brand => [brand, 0]))
  const cardBrands = new Set(Object.values(CardBrand).map(brand => brand.toLowerCase()))
  const cardBrandsToGroup = [CardBrand.mastercard, CardBrand.visa] //to handle brands with -> (international)

  for (const [key, value] of map.entries()) {
    const lowerKey = key.toLowerCase()
    let wasUpdateByInclude = false

    cardBrandsToGroup.forEach(brand => {
      if (lowerKey.includes(brand.toLowerCase())) {
        groupedMap.set(brand, (groupedMap.get(brand) || 0) + value)
        wasUpdateByInclude = true
      }
    })

    if (!wasUpdateByInclude) {
      if (cardBrands.has(lowerKey)) {
        groupedMap.set(key, value)
      } else {
        groupedMap.set(CardBrand.otras, (groupedMap.get(CardBrand.otras) || 0) + value)
      }
    }
  }

  groupedMap.entries().forEach(([key, value]) => {
    if (value === 0) {
      groupedMap.delete(key);
    }
  })

  return groupedMap
}

const translateDay = (key: number) => {
  return dayTranslateMap.get(key) || key.toString()
}

const translateMonth = (key: number) => {
  return monthTranslateMap.get(key) || key.toString()
}

const formatHourNumber = (hour: number) => {
  const period = hour >= 12 ? 'PM' : 'AM'
  return `${hour}:00 ${period}`
}

const convertToBarChartData = (timeFrame: TimeFrameChart, groupMap: Map<string | number, number>, collator: Intl.Collator) => {
  // MONTH se prellena con los días transcurridos del mes (ver getConfigByTimeFrame) para que se
  // vean aunque no tengan transacciones; el resto de los timeframes solo muestran lo que
  // sí tuvo movimiento.
  let data = Array.from(groupMap.entries())
  if (timeFrame !== TimeFrameChart.MONTH) {
    data = data.filter(([_, transactions]) => transactions !== 0)
  }

  if (timeFrame === TimeFrameChart.DAY || timeFrame === TimeFrameChart.MONTH) {
    data = data.sort(([keyA], [keyB]) => {
      if (typeof keyA === 'number' && typeof keyB === 'number')
        return keyA - keyB
      else
        return collator.compare(keyA.toString(), keyB.toString())
    });
  }

  return data.map(([timePeriod, transactions]) => {
    if (timeFrame === TimeFrameChart.DAY && typeof timePeriod === 'number') {
      timePeriod = formatHourNumber(timePeriod)
    } else if (timeFrame === TimeFrameChart.MONTH && typeof timePeriod === 'string') {
      // "MM-dd" -> solo el día, para mostrar en el eje (p. ej. "07-16" -> "16").
      timePeriod = String(Number(timePeriod.split('-')[1]))
    } else {
      timePeriod = timePeriod.toString()
    }
    return {
      timePeriod,
      transactions
    }
  })
}

